/**
 * Tier-4 statuses on enemies and the ground zones (M21, docs/TOWERS_V2.md "Tier 4"): stuns, one damage-over-time slot,
 * polymorph, and the patches of thorns, fire, plague, quaking and napalm a few skills leave on the road.
 */
import { BossMechanic, DamageType } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared } from "../../world/roadGeometry";
import { Enemy, GroundZone, Tower, ZoneKind } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { affixedSlow } from "../affixes";
import { Immunity } from "../../data/spawnRules";
import { applyDamage } from "../systems/combat";

/** Clears a fresh enemy's tier-4 statuses (spawns reuse slots). */
export function clearBranchStatuses(e: Enemy): void {
  e.stunTicksLeft = 0; e.dotPerTick = Fix64.Zero; e.dotTicksLeft = 0; e.dotSourceId = 0; e.sheepTicksLeft = 0;
  e.auraSlow = Fix64.Zero; e.frostbiteTicks = 0; e.ambushed = false;
}

/**
 * Bosses shrug off control: no stun, root, freeze, polymorph or banish takes them (the Colossus also ignores slows). A rare
 * spawn does not (Kevin 2026-10-25: rares were too hard to kill): control takes it as it takes any raider.
 */
export function controllable(e: Enemy): boolean { return !e.isBoss || e.isRare; }

/** Stuns (freezes, roots, pins) a non-boss for `ticks`: it stands still. A longer stun already on it is kept. Frostbound halves it. */
export function stun(sim: LevelSim, e: Enemy, source: Tower, ticks: number): void {
  if (!e.alive || !controllable(e) || ticks <= 0) return;
  const t = affixedSlow(sim, e, Fix64.fromInt(ticks)).floorToInt();
  if (t <= e.stunTicksLeft) return;
  e.stunTicksLeft = t;
  sim.events.add(SimEventKind.EnemyStunned, e.id, source.id, t);
}

/**
 * Puts a damage-over-time on `e` (poison, bleed): `perTick` magic damage for `ticks`, credited to `source`. One slot: the
 * same source refreshes it; another source takes it only if it hits harder per tick.
 */
export function applyDot(e: Enemy, source: Tower, perTick: Fix64, ticks: number): void {
  if (!e.alive || !perTick.isPositive() || ticks <= 0) return;
  if (e.dotTicksLeft > 0 && e.dotSourceId !== source.id && e.dotPerTick.gt(perTick)) return;
  e.dotPerTick = perTick; e.dotTicksLeft = ticks; e.dotSourceId = source.id;
}

/** Applies a slow from an aura or zone for this tick (the strongest wins; Frostbound halves it; the Colossus ignores it). */
export function auraSlowOn(sim: LevelSim, e: Enemy, fraction: Fix64): void {
  if (sim.balance.enemies[e.archetype].bossMechanic === BossMechanic.Colossus || (e.immunity & Immunity.Slow) !== 0) return;   // M36: immune to slows
  const s = affixedSlow(sim, e, fraction);
  if (s.gt(e.auraSlow)) e.auraSlow = s;
}

/** Lays a zone (ZoneCreated); it works from the next tick. */
export function layZone(sim: LevelSim, t: Tower, kind: ZoneKind, x: Fix64, y: Fix64, radius: Fix64, damagePerTick: Fix64, slow: Fix64, ticks: number): void {
  if (ticks <= 0 || !radius.isPositive()) return;
  const z: GroundZone = { id: sim.nextZoneId++, towerId: t.id, kind, x, y, radius, damagePerTick, slow, ticks, ticksLeft: ticks };
  sim.zones.push(z);
  sim.events.add(SimEventKind.ZoneCreated, z.id, t.id, kind);
}

/**
 * Once per wave tick, before the towers fire: the per-tick aura slows reset, damage-over-time and polymorph run down, and
 * every zone works on the ground enemies inside it then runs down (ZoneExpired). Zones are in laying order.
 */
export function tickBranchStatuses(sim: LevelSim): void {
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    e.auraSlow = Fix64.Zero;
    if (e.sheepTicksLeft > 0) e.sheepTicksLeft--;
    if (e.dotTicksLeft > 0) {
      e.dotTicksLeft--;
      const src = sim.findTower(e.dotSourceId);
      applyDamage(sim, e, src, e.dotPerTick, DamageType.Magic);
    }
  }
  if (sim.zones.length === 0) return;
  let kept = 0;
  for (let i = 0; i < sim.zones.length; i++) {
    const z = sim.zones[i];
    const src = sim.findTower(z.towerId);
    const r2 = z.radius.mul(z.radius);
    const fire = z.kind === ZoneKind.Firestorm || z.kind === ZoneKind.Napalm;
    sim.areaDepth++;                                          // a zone (poison cloud, napalm, firestorm) is area damage
    for (const e of sim.enemySlots) {
      if (!e.alive || e.isAir || e.burrowed || distanceSquared(z.x, z.y, e.x, e.y).gt(r2)) continue;   // underground: out of reach
      if (z.slow.isPositive()) auraSlowOn(sim, e, z.slow);
      if (z.damagePerTick.isPositive()) applyDamage(sim, e, src, z.damagePerTick, fire ? DamageType.Burn : DamageType.Magic);
    }
    sim.areaDepth--;
    z.ticksLeft--;
    if (z.ticksLeft > 0) { sim.zones[kept++] = z; continue; }
    sim.events.add(SimEventKind.ZoneExpired, z.id, z.towerId, z.kind);
  }
  sim.zones.length = kept;
}

/** Ends every zone at once (a stage cleared: nothing is left to stand in them). */
export function clearZones(sim: LevelSim): void {
  for (const z of sim.zones) sim.events.add(SimEventKind.ZoneExpired, z.id, z.towerId, z.kind);
  sim.zones.length = 0;
}

/** Turns a non-boss into a sheep for `ticks` (EnemyPolymorphed). */
export function polymorph(sim: LevelSim, e: Enemy, source: Tower, ticks: number): void {
  if (!e.alive || !controllable(e) || ticks <= 0) return;
  e.sheepTicksLeft = Math.max(e.sheepTicksLeft, ticks);
  sim.events.add(SimEventKind.EnemyPolymorphed, e.id, source.id, ticks);
}
